import { readonly, ref, shallowRef, type ShallowRef } from 'vue';

import { Novu, type Context, type StandardNovuOptions } from '@novu/js';

import {
  peanudsApi,
  withAuthenticatedHeaders,
  type BootstrapResponseDtoOutput,
  type GetNovuBootstrapInfoData,
} from '@peanuds/shared';
import {
  reportBackendFailure,
  reportZodValidationError,
} from '@peanuds/shared/observability/runtime-observability';
import { useCustomerConfigStore } from '@peanuds/shared-state';

/**
 * Singleton Novu JS SDK client.
 *
 * Lifecycle:
 *  - Call `initialize()` after login and whenever the session tokens change.
 *  - Call `disconnect()` on logout or before switching organization.
 *
 * The composable fetches bootstrap credentials from the backend
 * (`peanudsApi.getNovuBootstrapInfo`) and creates a headless `Novu` instance
 * that the notifications store (and any future consumer) uses for
 * listing, reading, and receiving real-time notifications. The instance is
 * recreated only when the bootstrap session (subscriber, context, hashes)
 * changes, and only the most recent `initialize()` call may apply its result.
 *
 * Bootstrap is retried up to `MAX_RETRIES` times with exponential backoff.
 */

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000; // 1s, 2s, 4s

export type NovuInitializationResult =
  'initialized' | 'subscriber-missing' | 'failed' | 'superseded';

// Module-level singleton so every call to useNovuClient() shares the same instance.
const novuInstance = shallowRef<Novu | null>(null);
const isInitialized = ref(false);
const initError = ref<unknown>(null);
let activeSessionKey: string | null = null;
let sessionGeneration = 0;

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const waitBeforeRetry = async (attempt: number, error: unknown) => {
  const delay = BASE_DELAY_MS * 2 ** attempt;
  console.warn(
    `[useNovuClient] Bootstrap error (attempt ${attempt + 1}/${
      MAX_RETRIES + 1
    }), retrying in ${delay}ms…`,
    error,
  );
  await wait(delay);
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getErrorStatusCode = (error: unknown): number | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  const statusCode = error.statusCode ?? error.status;
  return typeof statusCode === 'number' ? statusCode : undefined;
};

const getErrorMessage = (error: unknown): string | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  if (typeof error.message === 'string') {
    return error.message;
  }

  const nestedError = error.error;
  if (isRecord(nestedError) && typeof nestedError.message === 'string') {
    return nestedError.message;
  }

  return undefined;
};

const getSdkError = (result: Error): unknown => {
  if (!isRecord(result)) {
    return result;
  }

  return result.error ?? result;
};

const isMissingSubscriberError = (error: unknown) => {
  if (getErrorStatusCode(error) === 404) {
    return true;
  }

  const message = getErrorMessage(error)?.toLowerCase();
  return message?.includes('subscriber') === true && message.includes('not found');
};

const resolveNovuContext = (
  bootstrap: BootstrapResponseDtoOutput,
): Pick<StandardNovuOptions, 'context' | 'contextHash'> => {
  if (bootstrap.context) {
    return {
      context: bootstrap.context,
      ...(bootstrap.contextHash ? { contextHash: bootstrap.contextHash } : {}),
    };
  }

  const customerConfigStore = useCustomerConfigStore();
  const novuContext: Context | undefined = customerConfigStore.notifications.novuContext;
  const { novuContextHash } = customerConfigStore.notifications;

  return {
    ...(novuContext ? { context: novuContext } : {}),
    ...(novuContextHash ? { contextHash: novuContextHash } : {}),
  };
};

const toNovuOptions = (bootstrap: BootstrapResponseDtoOutput): StandardNovuOptions => ({
  applicationIdentifier: bootstrap.applicationIdentifier,
  subscriberId: bootstrap.subscriberId,
  subscriberHash: bootstrap.subscriberHash,
  apiUrl: bootstrap.apiUrl,
  socketUrl: bootstrap.socketUrl,
  ...resolveNovuContext(bootstrap),
  useCache: false,
});

const teardownInstance = () => {
  novuInstance.value?.socket.disconnect();
  novuInstance.value = null;
  activeSessionKey = null;
  isInitialized.value = false;
  initError.value = null;
};

export const useNovuClient = () => {
  const initialize = async (): Promise<NovuInitializationResult> => {
    const generation = ++sessionGeneration;
    const isSuperseded = () => generation !== sessionGeneration;

    console.log('[useNovuClient] 🔌 Starting Novu client initialization...');

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        console.debug(
          `[useNovuClient] Fetching bootstrap info (attempt ${attempt + 1}/${MAX_RETRIES + 1})...`,
        );
        const result = await peanudsApi.getNovuBootstrapInfo(
          await withAuthenticatedHeaders<GetNovuBootstrapInfoData>({}),
        );

        if (isSuperseded()) {
          return 'superseded';
        }

        if (result instanceof Error) {
          if (result.name === 'ZodError') {
            reportZodValidationError({
              error: result,
              area: 'notifications',
              operation: 'novu-bootstrap',
              source: 'useNovuClient',
            });
            initError.value = result;
            console.warn(
              '[useNovuClient] Bootstrap response could not be validated; treating as missing subscriber',
              result,
            );
            return 'subscriber-missing';
          }

          const error = getSdkError(result);
          initError.value = error;

          if (isMissingSubscriberError(error)) {
            console.debug('[useNovuClient] Bootstrap reported missing subscriber');
            return 'subscriber-missing';
          }

          if (attempt < MAX_RETRIES) {
            await waitBeforeRetry(attempt, error);
            continue;
          }

          reportBackendFailure({
            error,
            area: 'notifications',
            operation: 'novu-bootstrap',
            source: 'useNovuClient',
            tags: {
              attemptCount: attempt + 1,
            },
          });
          console.error(
            `[useNovuClient] Bootstrap failed after ${MAX_RETRIES + 1} attempts:`,
            error,
          );
          return 'failed';
        }

        const bootstrap = result.data;
        if (!bootstrap) {
          console.debug('[useNovuClient] Bootstrap returned empty data; subscriber may be missing');
          return 'subscriber-missing';
        }

        console.debug('[useNovuClient] ✅ Bootstrap data received:', {
          subscriberId: bootstrap.subscriberId,
          apiUrl: bootstrap.apiUrl,
          socketUrl: bootstrap.socketUrl,
          context: bootstrap.context,
        });

        const novuOptions = toNovuOptions(bootstrap);
        const sessionKey = JSON.stringify(novuOptions);

        if (novuInstance.value && sessionKey === activeSessionKey) {
          console.debug('[useNovuClient] Bootstrap session unchanged, keeping current client');
          initError.value = null;
          return 'initialized';
        }

        teardownInstance();
        novuInstance.value = new Novu(novuOptions);
        activeSessionKey = sessionKey;

        isInitialized.value = true;
        initError.value = null;
        console.log('[useNovuClient] 🎉 Novu client successfully initialized');
        return 'initialized'; // Success — exit retry loop
      } catch (error) {
        if (isSuperseded()) {
          return 'superseded';
        }

        initError.value = error;

        if (attempt < MAX_RETRIES) {
          await waitBeforeRetry(attempt, error);
          continue;
        }

        reportBackendFailure({
          error,
          area: 'notifications',
          operation: 'novu-bootstrap',
          source: 'useNovuClient',
          tags: {
            attemptCount: attempt + 1,
          },
        });
        console.error(
          `[useNovuClient] Failed to initialize Novu client after ${MAX_RETRIES + 1} attempts:`,
          error,
        );
      }
    }

    return 'failed';
  };

  const disconnect = () => {
    console.log('[useNovuClient] 🔌 Disconnecting Novu client');
    sessionGeneration++;
    teardownInstance();
  };

  return {
    novu: novuInstance as Readonly<ShallowRef<Novu | null>>,
    isInitialized: readonly(isInitialized),
    initError: readonly(initError),
    initialize,
    disconnect,
  };
};
